import type { ComboboxItem } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversationCommentAuthors } from '@/entities/conversations';
import { getShortName } from '@/shared/lib';
import { AllOrSpecificMultiSelect } from '@/shared/ui';

/**
 * Верхняя граница загружаемого списка авторов комментариев. Авторы — это проверяющие,
 * оставившие комментарии (ограниченный набор), поэтому список берётся целиком, а поиск в
 * выпадающем списке — клиентский.
 */
const AUTHORS_LIMIT = 100;

/** Пропсы выбора авторов комментариев (множественный выбор с пунктом «Все») */
export type CommentAuthorSelectProps = {
  /** Выбранные авторы; пустой массив — «Все» (все авторы) */
  value: number[];
  /** Смена набора авторов — управляющее действие уровня запроса (список перечитывается) */
  onChange: (authorIds: number[]) => void;
  className?: string;
};

/**
 * Выпадающий список выбора автора в поиске по комментариям (множественный выбор с галочками).
 * Показывается только в режиме поиска по комментариям. В списке — только реальные авторы
 * комментариев (эндпоинт авторов комментариев); подпись собирается из заполненных частей имени.
 */
export const CommentAuthorSelect: FC<CommentAuthorSelectProps> = ({ value, onChange, className }) => {
  const { t } = useTranslation(['dialogs', 'common']);
  const { data, isLoading } = useConversationCommentAuthors({ limit: AUTHORS_LIMIT });

  const options = useMemo<ComboboxItem[]>(
    () =>
      (data?.authors ?? []).map((author) => ({
        value: author.id,
        label:
          getShortName(author.firstName, author.lastName, author.middleName) ||
          t('dialogs:commentSearch.author.unnamed'),
      })),
    [data?.authors, t],
  );

  return (
    <AllOrSpecificMultiSelect
      options={options}
      value={value}
      onChange={onChange}
      allLabel={t('dialogs:commentSearch.author.all')}
      ariaLabel={t('dialogs:commentSearch.author.label')}
      searchPlaceholder={t('common:search')}
      emptyMessage={t('dialogs:commentSearch.author.empty')}
      isLoading={isLoading}
      className={className}
    />
  );
};
